<template>
  <v-card v-if="account" class="mb-3">
    <v-card-title class="d-flex align-center bg-primary text-white">
      <v-icon start class="mr-2">mdi-office-building</v-icon>
      <span class="flex-grow-1">{{ account.name || "Onbekende DG" }}</span>

      <!-- Refresh Button -->
      <v-btn
        icon
        variant="text"
        size="small"
        :loading="isRefreshing"
        class="ml-2"
        @click="refreshReports"
      >
        <v-icon>mdi-refresh</v-icon>
        <v-tooltip activator="parent" location="bottom">
          Rapporten vernieuwen
        </v-tooltip>
      </v-btn>

      <!-- Debug Info Button (only in development) -->
      <v-btn
        v-if="isDevelopment"
        icon
        variant="text"
        size="small"
        class="ml-1"
        @click="showDebugInfo = !showDebugInfo"
      >
        <v-icon>mdi-bug</v-icon>
        <v-tooltip activator="parent" location="bottom"> Debug info </v-tooltip>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <AccountListItem :account="account" hide-missing-data="true" />

      <v-divider></v-divider>

      <ReportButtons
        :account_id="account.id"
        :day_report_id="day_report.id"
        @report-created="handleReportCreated"
      />

      <v-divider></v-divider>

      <!-- Debug Info Section (development only) -->
      <v-expand-transition>
        <div
          v-if="showDebugInfo && isDevelopment"
          class="pa-3 bg-grey-lighten-4"
        >
          <h4 class="text-subtitle-2 mb-2">🐛 Debug Info</h4>
          <div class="text-caption">
            <div><strong>Account ID:</strong> {{ account.id }}</div>
            <div><strong>Day Report ID:</strong> {{ day_report.id }}</div>
            <div>
              <strong>Reports in Store:</strong>
              {{ reportsStore.reports.length }}
            </div>
            <div>
              <strong>Filtered Reports:</strong> {{ accountReports.length }}
            </div>
            <div>
              <strong>Store Loading:</strong> {{ reportsStore.isLoading }}
            </div>
            <div>
              <strong>Store Error:</strong> {{ reportsStore.error || "None" }}
            </div>
            <div class="mt-2">
              <strong>All Store Reports for Day Report:</strong>
              <pre class="text-caption">{{ debugDayReportReports }}</pre>
            </div>
            <div class="mt-2">
              <v-btn
                size="small"
                variant="outlined"
                :loading="testingApi"
                @click="testApiDirectly"
              >
                Test API Directly
              </v-btn>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <v-divider v-if="showDebugInfo && isDevelopment"></v-divider>

      <div class="pa-3">
        <div v-if="isLoadingReports" class="text-center py-4">
          <v-progress-circular
            indeterminate
            size="24"
            class="mr-2"
          ></v-progress-circular>
          <span class="text-caption">Rapporten laden...</span>
        </div>
        <div v-else-if="accountReports.length > 0">
          <ReportList :reports="accountReports" />
        </div>
        <v-alert
          v-else
          type="info"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          Geen rapporten gevonden voor deze DG in dit veldwerkverslag.
        </v-alert>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, ref } from "vue";
import ReportList from "../reports/ReportList.vue";
import ReportButtons from "../reports/ReportButtons.vue";
import AccountListItem from "./AccountListItem.vue";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";

export default {
  name: "AccountBox",
  components: {
    ReportList,
    AccountListItem,
    ReportButtons,
  },
  props: {
    account: {
      type: Object,
      required: true,
    },
    day_report: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const reportsStore = useReportsStoreRefactor();

    // Local reactive state
    const isRefreshing = ref(false);
    const showDebugInfo = ref(false);
    const testingApi = ref(false);

    // Environment detection
    const isDevelopment = computed(() => {
      return (
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost" ||
        window.location.hostname.includes("localhost")
      );
    });

    // Reactive computed that gets reports directly from store with real-time updates
    // Uses store getter for DRY principle - automatically updates when store changes (including ActionCable)
    const accountReports = computed(() => {
      if (!props.account?.id || !props.day_report?.id) {
        return [];
      }

      // Chronological order (oldest to newest) for AccountBox display
      return reportsStore.getReportsByAccountAndDayReport(
        props.account.id,
        props.day_report.id,
      );
    });

    // Enhanced loading state - more intelligent loading detection
    const isLoadingReports = computed(() => {
      const hasReports =
        accountReports.value && accountReports.value.length > 0;
      const storeLoading = reportsStore.isLoading;

      // Only show loading if:
      // 1. We don't have reports for this account/day_report combination
      // 2. AND the store is currently loading
      // 3. AND we're not in the middle of a create/update operation (which would show brief loading)
      return !hasReports && (storeLoading || isRefreshing.value);
    });

    // Debug computed to show all reports for this day report
    const debugDayReportReports = computed(() => {
      if (!props.day_report?.id) return [];

      return reportsStore.reports
        .filter(
          (report) =>
            report.day_report_id == props.day_report.id ||
            String(report.day_report_id) === String(props.day_report.id),
        )
        .map((report) => ({
          id: report.id,
          account_id: report.account_id,
          content: report.content?.substring(0, 50) + "...",
          date: report.date,
          created_at: report.created_at,
        }));
    });

    // Method to handle newly created reports with enhanced logging
    function handleReportCreated(newReport) {
      console.log(
        `AccountBox: Report created for account ${props.account.id} in day_report ${props.day_report.id}:`,
        newReport,
      );

      // The store will handle the update automatically via ActionCable or direct API response
      // The accountReports computed will reactively update when the store data changes

      // Optional: Force a brief refresh if the report doesn't appear immediately
      // This handles edge cases where ActionCable might be delayed
      setTimeout(() => {
        const currentReports = accountReports.value;
        const reportExists = currentReports.some((r) => r.id === newReport.id);

        if (
          !reportExists &&
          newReport.account_id == props.account.id &&
          newReport.day_report_id == props.day_report.id
        ) {
          console.log(
            `AccountBox: Report ${newReport.id} not found in store, may need manual refresh`,
          );
          // The store's ActionCable subscription should handle this, but this provides visibility
        }
      }, 1000);
    }

    // Refresh reports for this specific account and day report
    async function refreshReports() {
      if (!props.account?.id || !props.day_report?.id) {
        console.warn(
          "AccountBox: Cannot refresh - missing account or day_report",
        );
        return;
      }

      isRefreshing.value = true;
      console.log(
        `AccountBox: Refreshing reports for account ${props.account.id} in day_report ${props.day_report.id}`,
      );

      try {
        // Force refresh reports for this day report
        await reportsStore.getByDayReport(props.day_report.id, {
          forceRefresh: true,
          silent: false,
        });

        console.log(
          `AccountBox: Successfully refreshed reports. Found ${accountReports.value.length} reports for this account.`,
        );

        // Optional: Show a brief success indicator
        if (isDevelopment.value) {
          console.log("AccountBox: Refresh completed", {
            accountId: props.account.id,
            dayReportId: props.day_report.id,
            reportsFound: accountReports.value.length,
            allReportsInStore: reportsStore.reports.length,
          });
        }
      } catch (error) {
        console.error(`AccountBox: Error refreshing reports:`, error);
        // The error will be shown in the debug info if enabled
      } finally {
        isRefreshing.value = false;
      }
    }

    // Test API directly for debugging
    async function testApiDirectly() {
      if (!props.day_report?.id) {
        console.warn("AccountBox: Cannot test API - missing day_report");
        return;
      }

      testingApi.value = true;
      console.log(
        `🧪 Testing API directly for day_report ${props.day_report.id}`,
      );

      try {
        // Import apiClient dynamically to avoid circular imports
        const { default: apiClient } = await import("@/services/apiClient");

        const response = await apiClient.get(
          `/api/v1/day_reports/${props.day_report.id}/reports`,
          {},
          { showErrorAlert: false },
        );

        console.log("🧪 Direct API Response:", {
          type: typeof response,
          isArray: Array.isArray(response),
          hasData: response && "data" in response,
          dataType: response && typeof response.data,
          dataIsArray: response && Array.isArray(response.data),
          keys: response ? Object.keys(response) : "null/undefined",
          dataLength: response?.data?.length || "N/A",
          response: response,
        });

        // Show user-friendly info
        if (Array.isArray(response)) {
          console.log(
            `✅ Direct array response with ${response.length} reports`,
          );
        } else if (response?.data && Array.isArray(response.data)) {
          console.log(
            `✅ Paginated response with ${response.data.length} reports`,
          );
        } else {
          console.error("❌ Unexpected response format");
        }
      } catch (error) {
        console.error("🧪 Direct API test failed:", error);
      } finally {
        testingApi.value = false;
      }
    }

    return {
      // Data
      accountReports,
      isLoadingReports,
      isRefreshing,
      showDebugInfo,
      isDevelopment,
      debugDayReportReports,
      reportsStore,
      testingApi,

      // Methods
      handleReportCreated,
      refreshReports,
      testApiDirectly,
    };
  },
};
</script>
